import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Subject } from 'rxjs';
import ApplicationSettingsStringInput from './ApplicationSettingsStringInput';
import ApplicationSettingsNumberInput from './ApplicationSettingsNumberInput';
import { Card, Form, FormInstance } from 'antd';
import { IotaMetadataInput, SettingsMetadataSection, SettingsMetadata } from '../types';
import { ApplicationExternalSelectInput, ApplicationInternalSelectInputWrapper } from '.';
import ApplicationSettingsPasswordInput from './ApplicationSettingsPasswordInput';
import ApplicationSettingsObjectInput from './ApplicationSettingsObjectInput';
import ApplicationSettingsCheckboxInput from './ApplicationSettingsCheckboxInput';

interface Props {
    settingsForm: FormInstance;
    settingsJson: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    settingsValues?: { [id: string]: any };
    id: string;
    back: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    update: (id: string, settings: { [id: string]: any }) => void;
    disabled?: boolean;
}

interface Hash {
    [details: string]: string & string[] & number & number[];
}

export const ApplicationSettingsForm: React.FC<Props> = ({
    id,
    settingsForm,
    settingsJson,
    settingsValues,
    update,
    disabled = false
}) => {
    const formFieldsSubjectRef = React.useRef<Subject<Record<string, unknown>>>(new Subject<Record<string, unknown>>());

    const handleFormValuesChange = React.useCallback((changedValues: Record<string, unknown>) => {
        formFieldsSubjectRef.current.next(changedValues);
    }, []);

    const handleSubmit = async (values: Hash) => {
        if (settingsJson) {
            await update(id, values);
        }
    };

    const renderInput = (input: IotaMetadataInput) => {
        switch (input.type) {
            case 'string':
                return (
                    <ApplicationSettingsStringInput
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                    />
                );
            case 'number':
                return (
                    <ApplicationSettingsNumberInput
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                    />
                );
            case 'password':
                return (
                    <ApplicationSettingsPasswordInput
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                    />
                );
            case 'object':
                return (
                    <ApplicationSettingsObjectInput
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                    />
                );
            case 'checkbox':
                return (
                    <ApplicationSettingsCheckboxInput
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                    />
                );
            case 'externalSelect':
                return (
                    <ApplicationExternalSelectInput
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                        settingsValues={settingsValues}
                        formFieldsSubject={formFieldsSubjectRef.current}
                    />
                );
            case 'internalSelect':
                return (
                    <ApplicationInternalSelectInputWrapper
                        disabled={disabled}
                        form={settingsForm}
                        input={input}
                        key={input.id}
                        initialValue={getInputValue(input)}
                    />
                );
            default:
                return;
        }
    };

    const getInputValue = (input: IotaMetadataInput) => {
        if (settingsValues) {
            if (settingsValues[input.id]) {
                return settingsValues[input.id];
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    };

    const renderSection = (section: SettingsMetadataSection) => {
        return (
            <Card type="inner" title={section.name} key={section.name}>
                {section.inputs.map(i => renderInput(i))}
                {section.sections && section.sections.map(s => renderSection(s))}
            </Card>
        );
    };

    const renderAllSections = () => {
        if (settingsJson) {
            let settingsMetadata: SettingsMetadata = JSON.parse(settingsJson);
            return <>{settingsMetadata.sections && settingsMetadata.sections.map(s => renderSection(s))}</>;
        } else {
            return <div>No settings found</div>;
        }
    };

    return (
        <Form
            id="iota-app-settings-form"
            data-id="iota-edit-application-settings-form"
            form={settingsForm}
            layout="vertical"
            onFinish={handleSubmit}
            preserve={false}
            onValuesChange={handleFormValuesChange}
        >
            {renderAllSections()}
        </Form>
    );
};

export default observer(ApplicationSettingsForm);
